import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Chip,
  Button,
  CircularProgress
} from '@mui/material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import { 
  TrendingUp, 
  DollarSign,
  ArrowUpRight,
  ArrowDownRight,
  Download,
  Eye,
  EyeOff,
  Filter
} from 'lucide-react';
import { 
  BarChart,
  Bar,
  CartesianGrid, 
  XAxis, 
  YAxis, 
  Tooltip as RechartsTooltip, 
  ResponsiveContainer
} from 'recharts';

// Import cash flow service
import cashFlowService from '../../services/AGTUps-product-services/cashFlowService';

const appleColors = {
  light: {
    label: { primary: '#000000' },
    system: {
      grey: '#8E8E93',
      grey3: '#C7C7CC',
      green: '#31C859',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { primaryElevated: '#FFFFFF' },
    fill: { tertiary: '#767680' },
    ui: { accent: '#007BFE', base: '#F2F2F7' }
  },
  dark: {
    label: { primary: '#FFFFFF' },
    system: {
      grey: '#8E8E93',
      grey3: '#464649',
      green: '#2DD257',
      white: '#FFFFFF',
      black: '#000000'
    },
    bg: { primaryElevated: '#000000' },
    fill: { tertiary: '#767680' },
    ui: { accent: '#0385FF', base: '#272729' }
  }
};

const BalanceContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  height: 'calc(100vh - 180px)',
  overflowY: 'auto',
  '&::-webkit-scrollbar': { width: '8px' },
  '&::-webkit-scrollbar-track': { background: 'transparent' },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '4px'
  },
}));

const StyledPaper = styled(Paper)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(3),
    borderRadius: theme.spacing(2),
    height: '100%',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : (theme.palette.mode === 'dark'
        ? appleColors.dark.ui.base
        : appleColors.light.ui.base),
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    boxShadow: 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none'
  };
});

const MetricCard = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : (theme.palette.mode === 'dark'
        ? appleColors.dark.ui.base
        : appleColors.light.ui.base),
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    boxShadow: 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.2)' : 'none'
  };
});

const StyledSelect = styled(Select)(({ theme }) => {
  const mode = theme.palette.mode;
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : (mode === 'dark'
        ? appleColors.dark.ui.base
        : appleColors.light.ui.base),
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    borderRadius: '12px',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.2)' : 'none',
    '& .MuiSelect-select': {
      padding: '12px 16px',
      fontSize: '0.95rem',
      fontWeight: 500
    },
    '&:hover': {
      border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none'
    },
    '& fieldset': {
      border: 'none'
    }
  };
});

const BalanceOverview = () => {
  const theme = useTheme();
  const [timeRange, setTimeRange] = useState('6m');
  const [showBalance, setShowBalance] = useState(true);
  const [cashFlowData, setCashFlowData] = useState([]);
  const [dailyFlowData, setDailyFlowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dailyFilter, setDailyFilter] = useState('today');
  const [insightsLoading, setInsightsLoading] = useState(false);
  const [financialInsights, setFinancialInsights] = useState(null);

  // Función para filtrar datos diarios según el filtro seleccionado
  const getFilteredDailyData = () => {
    const today = new Date();
    const getDateStr = (date) => date.toISOString().split('T')[0];

    let fromStr, toStr;
    switch (dailyFilter) {
      case 'today': {
        const todayStr = getDateStr(today);
        fromStr = todayStr;
        toStr = todayStr;
        break;
      }
      case '1 week': {
        const fromDate = new Date();
        fromDate.setDate(today.getDate() - 7);
        fromStr = getDateStr(fromDate);
        toStr = getDateStr(today);
        break;
      }
      case '2 weeks': {
        const fromDate = new Date();
        fromDate.setDate(today.getDate() - 14);
        fromStr = getDateStr(fromDate);
        toStr = getDateStr(today);
        break;
      }
      case '3 weeks': {
        const fromDate = new Date();
        fromDate.setDate(today.getDate() - 21);
        fromStr = getDateStr(fromDate);
        toStr = getDateStr(today);
        break;
      }
      case '4 weeks': {
        const fromDate = new Date();
        fromDate.setDate(today.getDate() - 28);
        fromStr = getDateStr(fromDate);
        toStr = getDateStr(today);
        break;
      }
      case 'monthly': {
        const fromDate = new Date();
        fromDate.setDate(today.getDate() - 30);
        fromStr = getDateStr(fromDate);
        toStr = getDateStr(today);
        break;
      }
      default: {
        const fromDate = new Date();
        fromDate.setDate(today.getDate() - 7);
        fromStr = getDateStr(fromDate);
        toStr = getDateStr(today);
        break;
      }
    }
    return dailyFlowData.filter(d => d.period >= fromStr && d.period <= toStr);
  };

  // Calculamos el current balance acumulando el neto de cada registro mensual
  const currentBalance = cashFlowData.reduce(
    (acc, record) => acc + (record.income || 0) - (record.expenses || 0),
    0
  );

  // Calculamos el multiplicador en función del número de registros
  const count = cashFlowData.length;
  let multiplier = 1;
  if (count >= 200) {
    multiplier = 1.2;
  } else if (count >= 100) {
    multiplier = 1.15;
  } else if (count >= 50) {
    multiplier = 1.1;
  } else if (count >= 20) {
    multiplier = 1.05;
  } else {
    multiplier = 1;
  }
  const projectedBalance = currentBalance * multiplier;

  // Generamos nuevos datos para el gráfico, inyectando currentBalance en cada registro,
  // de modo que el gráfico de "Balance" (la barra morada/azulada) refleje el currentBalance.
  const chartData = cashFlowData.map(record => ({
    ...record,
    currentBalance: currentBalance
  }));

  // Función para cargar cash flow (mensual y diario)
  useEffect(() => {
    const fetchCashFlow = async () => {
      setLoading(true);
      try {
        const monthlyResult = await cashFlowService.getCashFlow({ range: timeRange, aggregation: 'monthly' });
        const dailyResult = await cashFlowService.getDailyCashFlow({ range: timeRange });
        if (monthlyResult.success && dailyResult.success) {
          setCashFlowData(monthlyResult.data);
          setDailyFlowData(dailyResult.data);
        }
      } catch (error) {
        console.error('Error fetching cash flow:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCashFlow();
  }, [timeRange]);

  // Efecto para obtener automáticamente el último insight generado al cargar la página
  useEffect(() => {
    const fetchLatestInsight = async () => {
      try {
        const insightResult = await cashFlowService.getLatestInsight();
        if (insightResult.success && insightResult.data) {
          setFinancialInsights(insightResult.data);
        }
      } catch (error) {
        console.error('Error fetching latest insight:', error);
      }
    };
    fetchLatestInsight();
  }, []);

  // Función para generar insights financieros utilizando Claude
  const handleGenerateInsights = async () => {
    setInsightsLoading(true);
    try {
      const response = await cashFlowService.generateClaudeInsights();
      if (response.success) {
        setFinancialInsights(response.data);
      } else {
        console.error('Error generating insights:', response.error);
      }
    } catch (error) {
      console.error('Error generating insights:', error);
    } finally {
      setInsightsLoading(false);
    }
  };

  // Calcular métricas para "Quick Stats" usando los datos filtrados diarios
  const filteredDaily = getFilteredDailyData();
  const totalDailyBalance = filteredDaily.reduce((acc, day) => acc + (day.balance || 0), 0);
  const avgDailyBalance = filteredDaily.length > 0 ? totalDailyBalance / filteredDaily.length : 0;
  let growthPercentage = 0;
  if (cashFlowData.length >= 2) {
    const lastPeriod = cashFlowData[cashFlowData.length - 1];
    const previousPeriod = cashFlowData[cashFlowData.length - 2];
    if ((previousPeriod.balance || 0) !== 0) {
      growthPercentage =
        ((lastPeriod.balance - previousPeriod.balance) / previousPeriod.balance) * 100;
    }
  }

  if (loading) {
    return (
      <BalanceContainer>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
          }}
        >
          <CircularProgress />
        </Box>
      </BalanceContainer>
    );
  }  

  return (
    <BalanceContainer>
      {/* Control Panel */}
      <Grid container spacing={2} alignItems="center" sx={{ marginBottom: 2 }}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth variant="outlined">
            <InputLabel
              sx={{
                color: theme.palette.mode === 'dark'
                  ? appleColors.dark.label.primary
                  : appleColors.light.label.primary,
                '&.Mui-focused': {
                  color: theme.palette.actualMode === 'wallpaper'
                    ? '#FFFFFF'
                    : (theme.palette.mode === 'dark'
                      ? appleColors.dark.ui.accent
                      : appleColors.light.ui.accent)
                }
              }}
            >
              Time Range
            </InputLabel>
            <StyledSelect
              value={timeRange}
              label="Time Range"
              onChange={(e) => setTimeRange(e.target.value)}
            >
              <MenuItem value="1m">Last Month</MenuItem>
              <MenuItem value="3m">Last 3 Months</MenuItem>
              <MenuItem value="6m">Last 6 Months</MenuItem>
              <MenuItem value="1y">Last Year</MenuItem>
            </StyledSelect>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={8} className="flex justify-end gap-2">
          <Tooltip title="Filter">
            <IconButton
              sx={{
                color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : 'inherit',
                '&:hover': {
                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <Filter size={20} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download Report">
            <IconButton
              sx={{
                color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : 'inherit',
                '&:hover': {
                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              <Download size={20} />
            </IconButton>
          </Tooltip>
          <Tooltip title={showBalance ? "Hide Balance" : "Show Balance"}>
            <IconButton
              onClick={() => setShowBalance(!showBalance)}
              sx={{
                color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : 'inherit',
                '&:hover': {
                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.04)'
                }
              }}
            >
              {showBalance ? <EyeOff size={20} /> : <Eye size={20} />}
            </IconButton>
          </Tooltip>
        </Grid>
      </Grid>

      {/* Balance Overview */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <StyledPaper>
            <Box className="flex justify-between items-center mb-6">
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                Cash Flow Overview
              </Typography>
              {/* Labels */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Chip 
                  size="small" 
                  icon={<ArrowUpRight size={14} />} 
                  label="Income" 
                  color="success"
                  sx={{ fontSize: '0.875rem' }}
                />
                <Chip 
                  size="small" 
                  icon={<ArrowDownRight size={14} />} 
                  label="Expenses" 
                  color="error"
                  sx={{ fontSize: '0.875rem' }}
                />
                <Chip 
                  size="small" 
                  icon={<DollarSign size={14} />} 
                  label="Balance" 
                  color="primary"
                  sx={{ fontSize: '0.875rem' }}
                />
              </Box>
            </Box>
            {/* Main Chart (monthly overview) */}
            <Box sx={{ mt: 2, height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="period" />
                  <YAxis />
                  <RechartsTooltip />
                  <Bar dataKey="income" fill="#059669" />
                  <Bar dataKey="expenses" fill="#dc2626" />
                  <Bar dataKey="currentBalance" fill="#4f46e5" />
                </BarChart>
              </ResponsiveContainer>
            </Box>
            {/* Working Capital & Liquidity Forecast */}
            <Box
              sx={{
                mt: 3,
                p: 2,
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
                borderRadius: 1
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                Working Capital & Liquidity Forecast
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Working Capital
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      $125K
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Liquidity Ratio
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      1.8
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={4}>
                  <Box sx={{ textAlign: 'center' }}>
                    <Typography variant="caption" color="text.secondary">
                      Cash Conversion
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      42 Days
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            {/* Explanatory Details */}
            <Box sx={{ mt: 3 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: theme.palette.mode === 'dark' ? '#1e3a8a' : '#dbeafe',
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Working Capital
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      The difference between current assets and current liabilities, indicating your operational liquidity.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: theme.palette.mode === 'dark' ? '#065f46' : '#d1fae5',
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Liquidity Ratio
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Measures your ability to cover short-term obligations with current assets.
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Box
                    sx={{
                      p: 2,
                      backgroundColor: theme.palette.mode === 'dark' ? '#854d0e' : '#fef3c7',
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                      Cash Conversion
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      The time it takes to convert investments in inventory into cash from sales.
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </StyledPaper>
        </Grid>

        <Grid item xs={12} md={4}>
          <Grid container spacing={3}>
            {/* Current Balance */}
            <Grid item xs={12}>
              <StyledPaper>
                <Typography variant="h6" sx={{ fontWeight: 600 }} gutterBottom>
                  Current Balance
                </Typography>
                <Box className="space-y-4">
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Available Balance
                    </Typography>
                    <Typography variant="h4">
                      {showBalance ? `$${currentBalance.toLocaleString()}` : '••••••'}
                    </Typography>
                    <Box className="flex items-center gap-1 mt-1">
                      <TrendingUp size={16} style={{ color: appleColors.light.system.green }} />
                      <Typography variant="body2" color="success.main">
                        +15% vs last month
                      </Typography>
                    </Box>
                  </Box>
                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Projected Balance
                    </Typography>
                    <Typography
                      variant="h4"
                      sx={{ color: (theme) => theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.primary.main }}
                    >
                      {showBalance ? `$${projectedBalance.toLocaleString()}` : '••••••'}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      End of period
                    </Typography>
                  </Box>
                </Box>
              </StyledPaper>
            </Grid>

            {/* Daily Cash Flow */}
            <Grid item xs={12}>
              <StyledPaper>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Daily Cash Flow
                  </Typography>
                  <FormControl variant="outlined" size="small">
                    <StyledSelect
                      value={dailyFilter}
                      onChange={(e) => setDailyFilter(e.target.value)}
                      sx={{
                        '& .MuiSelect-select': { padding: '4px 8px', fontSize: '0.85rem' }
                      }}
                    >
                      <MenuItem value="today">Today</MenuItem>
                      <MenuItem value="1 week">1 Week</MenuItem>
                      <MenuItem value="2 weeks">2 Weeks</MenuItem>
                      <MenuItem value="3 weeks">3 Weeks</MenuItem>
                      <MenuItem value="4 weeks">4 Weeks</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </StyledSelect>
                  </FormControl>
                </Box>
                <Box sx={{ height: 200, mt: 2 }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={getFilteredDailyData()}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="period" />
                      <YAxis />
                      <RechartsTooltip />
                      <Bar dataKey="income" fill="#059669" name="Income" />
                      <Bar dataKey="expenses" fill="#dc2626" name="Expenses" />
                      <Bar dataKey="balance" fill="#4f46e5" name="Balance" />
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </StyledPaper>
            </Grid>

            {/* Quick Stats */}
            <Grid item xs={12}>
              <StyledPaper>
                <Typography variant="h6" sx={{ fontWeight: 600 }} gutterBottom>
                  Quick Stats
                </Typography>
                <Grid container spacing={2}>
                  {/* Net Balance */}
                  <Grid item xs={12} sm={4}>
                    <MetricCard>
                      <Box className="flex items-center gap-2 mb-1">
                        <DollarSign size={20} style={{ color: appleColors.light.system.green }} />
                        <Typography variant="body2">Net Balance</Typography>
                      </Box>
                      <Typography variant="h6">
                        {showBalance ? `$${currentBalance.toLocaleString()}` : '••••••'}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : theme.palette.primary.main
                        }}
                      >
                        Income - Expenses
                      </Typography>
                    </MetricCard>
                  </Grid>
                  {/* Daily Average */}
                  <Grid item xs={12} sm={4}>
                    <MetricCard>
                      <Box className="flex items-center gap-2 mb-1">
                        <TrendingUp size={20} style={{ color: '#059669' }} />
                        <Typography variant="body2">Daily Average</Typography>
                      </Box>
                      <Typography variant="h6">
                        {showBalance
                          ? `$${avgDailyBalance.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                          : '••••••'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Average daily balance
                      </Typography>
                    </MetricCard>
                  </Grid>
                  {/* Growth Rate */}
                  <Grid item xs={12} sm={4}>
                    <MetricCard>
                      <Box className="flex items-center gap-2 mb-1">
                        <ArrowUpRight
                          size={20}
                          style={{ color: growthPercentage >= 0 ? '#059669' : '#dc2626' }}
                        />
                        <Typography variant="body2">Growth Rate</Typography>
                      </Box>
                      <Typography variant="h6" color={growthPercentage >= 0 ? 'success.main' : 'error.main'}>
                        {showBalance ? `${growthPercentage.toFixed(2)}%` : '••••••'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Change vs previous period
                      </Typography>
                    </MetricCard>
                  </Grid>
                </Grid>
              </StyledPaper>
            </Grid>
          </Grid>
        </Grid>

        {/* Alerts and Insights */}
        <Grid item xs={12}>
          <StyledPaper>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Financial Insights
              </Typography>
              <Button
                variant="contained"
                disableElevation
                onClick={handleGenerateInsights}
                disabled={insightsLoading}
                sx={{
                  backgroundColor: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : '#0385FF',
                  color: theme.palette.actualMode === 'wallpaper' ? '#000000' : '#FFFFFF',
                  borderRadius: '30px',
                  textTransform: 'none',
                  px: 2,
                  py: 1,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: theme.palette.actualMode === 'wallpaper'
                      ? 'rgba(255, 255, 255, 0.9)'
                      : '#0266cc',
                    boxShadow: 'none',
                  },
                }}
              >
                {insightsLoading ? <CircularProgress size={24} color="inherit" /> : 'Generate insights with AGT'}
              </Button>
            </Box>
            {financialInsights && financialInsights.insights ? (
              <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Liquidity
                </Typography>
                <Box
                  sx={{
                    backgroundColor: theme.palette.mode === 'dark' ? '#1e3a8a' : '#dbeafe',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Typography variant="body2">
                    Current Liquidity: {financialInsights.insights.liquidity.currentLiquidity}
                  </Typography>
                  <Typography variant="body2">
                    Recommendations: {financialInsights.insights.liquidity.recommendations.join(', ')}
                  </Typography>
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Expense Control
                </Typography>
                <Box
                  sx={{
                    backgroundColor: theme.palette.mode === 'dark' ? '#065f46' : '#d1fae5',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Typography variant="body2">
                    Suggestions: {financialInsights.insights.expenseControl.suggestions.join(', ')}
                  </Typography>
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Financing Recommendations
                </Typography>
                <Box
                  sx={{
                    backgroundColor: theme.palette.mode === 'dark' ? '#854d0e' : '#fef3c7',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Typography variant="body2">
                    Options: {financialInsights.insights.financingRecommendations.options.join(', ')}
                  </Typography>
                  <Typography variant="body2">
                    Notes: {financialInsights.insights.financingRecommendations.notes}
                  </Typography>
                </Box>

                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Summary
                </Typography>
                <Box
                  sx={{
                    backgroundColor: theme.palette.mode === 'dark' ? alpha(theme.palette.info.main, 0.2) : '#ede9fe',
                    borderRadius: 2,
                    p: 2,
                  }}
                >
                  <Typography variant="body2">
                    {financialInsights.insights.summary}
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No insights generated yet. Press the button above to generate liquidity insights based on your data.
              </Typography>
            )}
          </StyledPaper>
        </Grid>
      </Grid>
    </BalanceContainer>
  );
};

export default BalanceOverview;
